/**
 * The frame every full-screen surface on this display shares.
 *
 * Two rules it exists to enforce. The close button lives in the frame, never in
 * the scrolling content, so it can be reached at any scroll position (FR-010) —
 * on a screen with no touch scrolling, a close button that has scrolled away is
 * a trapped cook. And the frame is the full height of the display: this is a
 * wall screen, there is nothing behind the modal worth leaving visible.
 */
import { useEffect } from "react";
import { X } from "lucide-react";
import { useTranslation } from "../i18n";

interface ModalShellProps {
  title: React.ReactNode;
  /** Small line under the title: reference number, source, customer. */
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Actions pinned under the body — never scrolls away either. */
  footer?: React.ReactNode;
  closeLabel?: string;
}

export default function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  footer,
  closeLabel: closeLabelProp,
}: ModalShellProps) {
  const { t } = useTranslation();
  const closeLabel = closeLabelProp ?? t("kds.common.close", "Chiudi");
  // A keyboard is rare in a kitchen, but the display is also opened from a desk
  // when someone is diagnosing a service, and Escape is what they will press.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/80 p-2 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={[
          "bg-kds-surface rounded-2xl border border-kds-muted/30 shadow-2xl",
          // Full height of the display, and wide enough that a dish line —
          // quantity, name, variant, note — reads without wrapping (FR-006).
          "w-full max-w-5xl h-full flex flex-col overflow-hidden",
        ].join(" ")}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-kds-muted/20 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-kds-title text-kds-text leading-tight truncate">
              {title}
            </h2>
            {subtitle && (
              <div className="text-kds-meta text-kds-muted mt-1">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 rounded-xl bg-kds-card text-kds-text text-kds-action font-bold min-h-touch min-w-touch hover:bg-kds-card/70 transition-colors"
            aria-label={closeLabel}
          >
            <X size={32} strokeWidth={3} />
            {closeLabel}
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">{children}</div>

        {footer && (
          <div className="flex-shrink-0 border-t border-kds-muted/20 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
